// The package's transport accessor. Every calls api module resolves its client
// through the `@heidi/api` configured-client registry (E5) rather than
// importing an app singleton, so `http()` returns the app's real
// `scribeHttpV2` and request behaviour is byte-identical.
//
// Shared by all five family api modules so the structural client type has one
// definition — the reminders carve declared it locally, and a second copy in
// each of four more modules would drift.
import { getScribeHttpClients } from '@heidi/api/client';

/**
 * Narrow structural view of the app's axios client — the package's public fn
 * signatures never name axios types (declaration portability).
 */
export interface CallsHttpClient {
  get<T>(
    url: string,
    config?: { params?: Record<string, unknown>; timeout?: number }
  ): Promise<{ data: T }>;
  post<T = unknown>(
    url: string,
    body?: unknown,
    config?: {
      timeout?: number;
      headers?: Record<string, string>;
      // WIDENED at the `calls/agents` D1 carve (ARCFE-1097) for
      // `previewCallVoice`, which asks axios for the raw `audio/wav` bytes.
      // Typed as the single literal it is used with rather than axios's full
      // `ResponseType` union: naming the union here would pull an axios type
      // into a structural interface whose whole purpose is not to name one.
      responseType?: 'arraybuffer';
    }
  ): Promise<{ data: T }>;
  put<T = unknown>(
    url: string,
    body?: unknown,
    config?: { timeout?: number }
  ): Promise<{ data: T }>;
  patch<T = unknown>(
    url: string,
    body?: unknown,
    config?: { timeout?: number; headers?: Record<string, string> }
  ): Promise<{ data: T }>;
  delete<T = unknown>(
    url: string,
    config?: { params?: Record<string, unknown>; data?: unknown }
  ): Promise<{ data: T }>;
}

export const http = (): CallsHttpClient => getScribeHttpClients().scribeHttpV2;
